// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useCallback, useState, useMemo, createContext, useContext } from 'react';
import { LOCAL_STORAGE_EXPERIMENTS_KEY, getJsonFromLocalStorage, saveJsonToLocalStorage } from 'utils/localStorage';

const saveExperimentSettingsToLocalStorage = (experiments: Partial<Experiments>): void => {
  const updatedExperiments = { ...(getExperimentSettingsFromLocalStorage() ?? {}), ...experiments };
  saveJsonToLocalStorage(LOCAL_STORAGE_EXPERIMENTS_KEY, updatedExperiments);
};

const getExperimentSettingsFromLocalStorage = (): Partial<Experiments> | undefined =>
  getJsonFromLocalStorage<Partial<Experiments>>(LOCAL_STORAGE_EXPERIMENTS_KEY);

export interface Experiment<T> {
  name: string;
  defaultValue: T;
  userValue?: T;
  activeValue: T;
}

interface Experiments {
  lobbyMusic: Experiment<boolean>;
  holidayThemes: Experiment<boolean>;
  // CTE: Experiment<boolean>;
}

interface ExperimentsContext {
  experimentValues: Experiments;
  updateExperimentValue: (experimentKey: keyof Experiments, userValue: any) => void;
}

const experimentSettingsFromLocalStorage = getExperimentSettingsFromLocalStorage();

const defaultExperimentValues: Experiments = {
  lobbyMusic: {
    name: 'Lobby Music',
    defaultValue: true,
    userValue: experimentSettingsFromLocalStorage?.lobbyMusic?.userValue,
    activeValue: experimentSettingsFromLocalStorage?.lobbyMusic?.userValue ?? true
  },
  holidayThemes: {
    name: 'Holiday Themes',
    defaultValue: true,
    userValue: experimentSettingsFromLocalStorage?.holidayThemes?.userValue,
    activeValue: experimentSettingsFromLocalStorage?.holidayThemes?.userValue ?? true
  }
  // CTE: {
  //   name: 'Enable Teams Identity Usage',
  //   defaultValue: true,
  //   userValue: experimentSettingsFromLocalStorage?.CTE?.userValue,
  //   activeValue: experimentSettingsFromLocalStorage?.CTE?.userValue ?? true
  // }
};

const ExperimentsContext = createContext<ExperimentsContext>({
  experimentValues: defaultExperimentValues,
  updateExperimentValue: () => {
    return;
  }
});

export interface ExperimentsProviderProps {
  children: React.ReactNode;
}

export const ExperimentProvider = (props: ExperimentsProviderProps): JSX.Element => {
  const { children } = props;

  const [experimentValues, setExperimentValues] = useState<Experiments>(defaultExperimentValues);

  const updateExperimentValue = useCallback((key: keyof Experiments, newValue: any): void => {
    setExperimentValues((prev) => {
      const updatedExperiments = {
        ...prev,
        [key]: {
          ...prev[key],
          userValue: newValue,
          activeValue: newValue ?? prev[key].defaultValue
        }
      };
      saveExperimentSettingsToLocalStorage(updatedExperiments);
      return updatedExperiments;
    });
  }, []);

  const state = useMemo<ExperimentsContext>(
    () => ({
      experimentValues,
      updateExperimentValue
    }),
    [experimentValues, updateExperimentValue]
  );

  return <ExperimentsContext.Provider value={state}>{children}</ExperimentsContext.Provider>;
};

export const useExperiments = (): ExperimentsContext => useContext(ExperimentsContext);
